import { Observable, throwError } from 'rxjs';
import { catchError, concatMap, map, take, tap } from 'rxjs/operators';

import { Checkout } from '@cart-checkout/data-access/interfaces';
import { CheckoutDao } from '@cart-checkout/data-access/dao';
import { GiftCard } from '@cart-checkout/checkout/data-access/interfaces';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CheckoutFlow } from '@cart-checkout/checkout/data-access/enums';
import { CookieService } from '@shared/utils/services';
import {
	ModernCheckoutCartEvaluatorService
} from './modern-checkout-cart-evaluator.service';
import { LoadingOverlayService, Store } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class GiftCardService {
	constructor(
		private checkoutDao: CheckoutDao,
		private store: Store,
		private cookieService: CookieService,
		private cartEvaluatorService: ModernCheckoutCartEvaluatorService,
		private loadingOverlayService: LoadingOverlayService,
		@Inject(PLATFORM_ID) private platformId
	) { }

	//region Wrapper Methods

	public applyGiftCardWrapper(checkoutKey: string, giftCard: GiftCard) {
		const modernCheckoutCookie = isPlatformBrowser(this.platformId) ? this.cookieService.get('modern').toLowerCase() === 'true' : false;
		const checkoutFlow = this.cartEvaluatorService.evaluateCookie(modernCheckoutCookie);
		if (checkoutFlow === CheckoutFlow.MODERN) {
			return this.applyModernGiftCard(checkoutKey, giftCard);
		} else {
			return this.applyGiftCard(checkoutKey, giftCard);
		}
	}

	public removeGiftCardWrapper(checkoutKey: string, giftCardKey: string) {
		const modernCheckoutCookie = isPlatformBrowser(this.platformId) ? this.cookieService.get('modern').toLowerCase() === 'true' : false;
		const checkoutFlow = this.cartEvaluatorService.evaluateCookie(modernCheckoutCookie);
		if (checkoutFlow === CheckoutFlow.MODERN) {
			return this.removeModernGiftCard(checkoutKey, giftCardKey);
		} else {
			return this.removeGiftCard(checkoutKey, giftCardKey);
		}
	}

	//endregion

	//region Legacy Checkout

	/**
	 * @description
	 * Adds a gift card for a given checkout. Upon successful addition,
	 * it will update the CHECKOUT object in the Store.
	 *
	 * @param checkoutKey
	 * @param giftCard
	 */
	public applyGiftCard(checkoutKey: string, giftCard: GiftCard): Observable<Checkout> {
		return this.checkoutDao.applyGiftCard(checkoutKey, giftCard)
			.pipe(
				tap((checkoutResponse: Checkout) => {
					this.store.set('CHECKOUT', checkoutResponse);
				})
			);
	}

	/**
	 * @description
	 * Removes a gift card by key for a given checkout. Upon successful removal,
	 * it will update the CHECKOUT object in the Store.
	 *
	 * @param checkoutKey
	 * @param giftCardKey
	 */
	public removeGiftCard(checkoutKey: string, giftCardKey: string): Observable<Checkout> {
		return this.checkoutDao.removeGiftCard(checkoutKey, giftCardKey)
			.pipe(
				tap((checkoutResponse: Checkout) => {
					this.store.set('CHECKOUT', checkoutResponse);
				})
			);
	}
	//endregion

	//region Modern Checkout

	/**
	 * @description
	 * Adds a gift card for a given modern checkout. Upon successful addition,
	 * it will update the CHECKOUT object in the Store.
	 *
	 * @param checkoutKey
	 * @param giftCard
	 */
	private applyModernGiftCard(checkoutKey: string, giftCard: GiftCard): Observable<Checkout> {
		return this.checkoutDao.applyModernGiftCard(checkoutKey, giftCard.card_number, giftCard.pin)
			.pipe(
				tap((checkoutResponse: Checkout) => {
					this.store.set('CHECKOUT', checkoutResponse);
				})
			);
	}

	/**
	 * @description
	 * Removes a gift card by key for a given modern checkout. Upon successful removal,
	 * it will update the CHECKOUT object in the Store.
	 *
	 * @param checkoutKey
	 * @param index
	 */
	private removeModernGiftCard(checkoutKey: string, index: string): Observable<Checkout> {
		return this.checkoutDao.removeModernGiftCard(checkoutKey, index)
			.pipe(
				tap((checkoutResponse: Checkout) => {
					this.store.set('CHECKOUT', checkoutResponse);
				})
			);
	}

	//endregion

	/**
	 * @description exposed function for payments to apply a gift card to checkout object for independence pay
	 * @param giftCard Independence Pay Gift Card
	 * @returns Checkout as a promise
	 */
	/* instanbul ignore next */
	public addIndPayGiftCard = (giftCard: GiftCard) => {
		let checkoutKey = null;
		this.loadingOverlayService.show();
		return this.store.selectDistinctKey('CHECKOUT', true).pipe(take(1), tap((checkout: Checkout) => {
			checkoutKey = checkout.checkout_key;
		}), concatMap(() => {
			return this.applyGiftCardWrapper(checkoutKey, giftCard)
				.pipe(
					map((checkout: Checkout) => {
						this.loadingOverlayService.hide();
						return {
							status: 'SUCCESS',
							checkout: checkout
						};
					}));
		}), catchError((err) => {
			this.loadingOverlayService.hide();
			let errorMessage = null;
			switch(err.status){
				case 406:
					errorMessage = 'The Gift Card Already Applied to the Order';
					break;
				case 410:
					errorMessage = 'Checkout Already Submitted';
					break;
				case 417:
					errorMessage = 'There is no remaining balance on this card';
					break;
				case 422:
					errorMessage = 'Checkout Redirect Required';
					break;
				case 426:
					errorMessage = 'Invalid CheckoutKey';
					break;
				case 428:
					errorMessage = '3DS Required';
					break;
				default:
					errorMessage = 'Bad Request';
					break;
			}
			return throwError({
				status: 'Fail',
				errors: [
					{
						message: errorMessage
					}
				]
			});
		})).toPromise();
	};

	/**
	 * @description exposed function for payments to remove a gift card to checkout object for independence pay
	 * @param externalKey Independence Pay Gift Card
	 * @returns Checkout as a promise
	 */
	/* instanbul ignore next */
	public removeIndPayGiftCard = (externalKey: string) => {
		let checkoutKey = null;
		this.loadingOverlayService.show();
		return this.store.selectDistinctKey('CHECKOUT', true).pipe(take(1), tap((checkout: Checkout) => {
			checkoutKey = checkout.checkout_key;
		}), concatMap(() => {
			return this.removeGiftCardWrapper(checkoutKey, externalKey)
				.pipe(
					map((checkout: Checkout) => {
						this.loadingOverlayService.hide();
						return {
							status: 'SUCCESS',
							checkout: checkout
						};
					}));
		}), catchError((err) => {
			this.loadingOverlayService.hide();
			let errorMessage = null;
			if (err.status === 406) {
				errorMessage = 'The Gift Card Not on the Order';
			} else {
				errorMessage = 'Bad Request';
			}
			return throwError({
				status: 'Fail',
				errors: [
					{
						message: errorMessage
					}
				]
			});
		})).toPromise();
	};
}
